import { parseMeasurement } from "./sheetModel";

export const DOOR_MATERIALS = [
  { value: "thermofoil_laminate", label: "Thermofoil / Laminate", rate: 40 },
  { value: "mdf_doors", label: "MDF Doors", rate: 25 },
  { value: "maple_doors", label: "Maple Doors", rate: 45 },
  { value: "white_oak_doors", label: "White Oak Doors", rate: 55 },
  { value: "rift_cut_white_oak_doors", label: "Rift-Cut White Oak Doors", rate: 65 },
];

export const GABLE_MATERIALS = [
  { value: "flat_mdf_panels", label: "Flat MDF Panels", rate: 20 },
  { value: "maple_gables", label: "Maple Gables", rate: 30 },
  { value: "white_oak_gables", label: "White Oak Gables", rate: 40 },
  { value: "rift_cut_white_oak_gables", label: "Rift-Cut White Oak Gables", rate: 50 },
];

export const TRIM_SECTIONS = ["crown_moulding", "light_valance", "riser_fascia"];
export const SURFACE_SECTIONS = ["doors_fronts", "gables_panels"];
export const SCRATCHPAD_SECTIONS = [
  { key: "cabinet_boxes", label: "Cabinet & Drawer Boxes", short: "Boxes" },
  { key: "doors_fronts", label: "Doors & Fronts", short: "Doors" },
  { key: "gables_panels", label: "Gables & Panels", short: "Gables" },
  { key: "crown_moulding", label: "Crown Moulding", short: "Crown" },
  { key: "light_valance", label: "Light Valance", short: "Valance" },
  { key: "riser_fascia", label: "Riser / Fascia", short: "Riser" },
];

export const DEFAULT_SCRATCHPAD_SETTINGS = { partner_discount_enabled: false, bill_calculated_at: null };
export const FRONT_FINISH_RATES = { paint: 25, stain: 40 };
export const BACK_FINISH_RATES = { paint: 25, stain: 35 };
export const BOX_TYPE_OPTIONS = [{ value: "", label: "—" }, ...["UPPER", "BASE", "PANTRY", "DRAWER", "DRW BX", "OTHER"].map(value => ({ value, label: value }))];
export const DOOR_TYPE_OPTIONS = ["DOOR", "DRAWER FRONT", "OTHER"].map(value => ({ value, label: value }));
export const WOOD_MATERIALS = new Set(["maple_doors", "white_oak_doors", "rift_cut_white_oak_doors", "maple_gables", "white_oak_gables", "rift_cut_white_oak_gables"]);
export const MDF_MATERIALS = new Set(["mdf_doors", "flat_mdf_panels"]);
const MATERIALS = [...DOOR_MATERIALS, ...GABLE_MATERIALS];
const MATERIAL_RATE_MAP = Object.fromEntries(MATERIALS.map(item => [item.value, item.rate]));
const MATERIAL_LABEL_MAP = Object.fromEntries(MATERIALS.map(item => [item.value, item.label]));
const FINISH_OPTIONS = [{ value: "none", label: "None" }, { value: "paint", label: "Paint" }, { value: "stain", label: "Stain" }];

export const materialLabel = (code) => MATERIAL_LABEL_MAP[code] || code || "—";
export const isTrimSection = (section) => TRIM_SECTIONS.includes(section);
const isThermofoil = (material) => material === "thermofoil_laminate";

export const applyMaterialSelection = (row, material) => {
  if (WOOD_MATERIALS.has(material)) return { ...row, material, finish_required: "stain", back_finish: true };
  if (MDF_MATERIALS.has(material)) return { ...row, material, finish_required: "paint", back_finish: false };
  if (isThermofoil(material)) return { ...row, material, finish_required: "none", back_finish: false };
  return { ...row, material };
};

export const newScratchpadRow = (section) => {
  const base = {
    row_id: `new_${Math.random().toString(36).slice(2, 10)}`, section, item_code: "", description: "", item_type: "",
    width: "", height: "", depth: "", quantity: 1, material: "", vendor: "", finish_required: "none", back_finish: false,
    value_per_foot: "", length: "", notes: "", dispatched_to: [], auto_generated: false, derived_from_box_id: null,
    derived_values: {}, derivation_overrides: {},
  };
  if (section === "doors_fronts") return applyMaterialSelection({ ...base, item_type: "DOOR" }, "mdf_doors");
  if (section === "gables_panels") return applyMaterialSelection(base, "flat_mdf_panels");
  return base;
};

export const normalizeScratchpadRow = (row) => {
  if (row.section === "cabinet_boxes") return { ...row, item_type: String(row.item_type || "").toUpperCase(), finish_required: "none", back_finish: false };
  if (isTrimSection(row.section)) return { ...row, finish_required: FINISH_OPTIONS.some(option => option.value === row.finish_required) ? row.finish_required : "none", back_finish: false };
  const typed = row.section === "doors_fronts" ? { ...row, item_type: String(row.item_type || "").toUpperCase() } : row;
  if (WOOD_MATERIALS.has(row.material)) return { ...typed, finish_required: "stain", back_finish: true };
  if (MDF_MATERIALS.has(row.material)) {
    const finish = ["none", "paint"].includes(row.finish_required) ? row.finish_required : "paint";
    return { ...typed, finish_required: finish, back_finish: finish === "paint" ? Boolean(row.back_finish) : false };
  }
  return { ...typed, finish_required: "none", back_finish: false };
};

const text = (key, label, width) => ({ key, label, type: "text", width });
const measure = (key, label, transformRow) => ({ key, label, type: "measure", width: 82, ...(transformRow ? { transformRow } : {}) });
const number = (key, label, width = 76, transformRow) => ({ key, label, type: "number", width, ...(transformRow ? { transformRow } : {}) });
const computed = (key, label, width, display) => ({ key, label, type: "computed", width, display });
const identity = [text("item_code", "Product Code", 126), text("description", "Item Description", 190)];
const quantity = number("quantity", "Qty", 62);
const notes = text("notes", "Notes", 180);
const finishColumn = {
  key: "finish_required", label: "Finish", type: "select", width: 92, options: FINISH_OPTIONS,
  optionsFor: row => WOOD_MATERIALS.has(row.material) ? [FINISH_OPTIONS[2]] : isThermofoil(row.material) ? [FINISH_OPTIONS[0]] : MDF_MATERIALS.has(row.material) ? FINISH_OPTIONS.slice(0, 2) : FINISH_OPTIONS,
  readOnly: row => WOOD_MATERIALS.has(row.material) || isThermofoil(row.material),
  transformRow: row => normalizeScratchpadRow(row),
};

export const rawSurfaceSqft = row => {
  const width = parseMeasurement(row.width), height = parseMeasurement(row.height), qty = Math.max(0, Number(row.quantity) || 0);
  return width > 0 && height > 0 ? width * height / 144 * qty : 0;
};
export const paddedFrontSqft = row => {
  const width = parseMeasurement(row.width), height = parseMeasurement(row.height), qty = Math.max(0, Number(row.quantity) || 0);
  return width > 0 && height > 0 ? (width + 2) * (height + 2) / 144 * qty : 0;
};
export const materialLineTotal = row => isTrimSection(row.section)
  ? (Number(row.value_per_foot) || 0) * (Number(row.length) || 0) * (Number(row.quantity) || 0)
  : rawSurfaceSqft(row) * (MATERIAL_RATE_MAP[row.material] || 0);
const moneyCell = value => value ? `$${value.toFixed(2)}` : "$0.00";

export const scratchpadColumnsFor = section => {
  if (section === "cabinet_boxes") return [
    ...identity, { key: "item_type", label: "Type", type: "select", width: 100, options: BOX_TYPE_OPTIONS, transformRow: row => ({ ...row, item_type: String(row.item_type || "").toUpperCase() }) },
    measure("width", 'W"'), measure("height", 'H"'), measure("depth", 'D"'), quantity, text("material", "Material", 140), text("vendor", "Vendor", 130), notes,
  ];
  if (isTrimSection(section)) return [
    ...identity, number("value_per_foot", "$ Value / Foot", 105),
    number("length", "Length (ft)", 92), quantity, text("vendor", "Vendor", 130), finishColumn,
    computed("line_total", "Line Total", 105, row => moneyCell(materialLineTotal(row))), notes,
  ];
  const materials = section === "doors_fronts" ? DOOR_MATERIALS : GABLE_MATERIALS;
  const markOverride = field => row => row.auto_generated ? { ...row, derivation_overrides: { ...(row.derivation_overrides || {}), [field]: true } } : row;
  const typeColumn = section === "doors_fronts"
    ? { key: "item_type", label: "Type", type: "select", width: 110, options: DOOR_TYPE_OPTIONS, transformRow: row => ({ ...row, item_type: String(row.item_type || "").toUpperCase() }) }
    : text("item_type", "Type", 100);
  return [
    ...identity, typeColumn,
    measure("width", 'Raw W"', section === "doors_fronts" ? markOverride("width") : null),
    measure("height", 'Raw H"', section === "doors_fronts" ? markOverride("height") : null),
    section === "doors_fronts" ? number("quantity", "Qty", 62, markOverride("quantity")) : quantity,
    { key: "material", label: "Material", type: "select", width: 190, options: materials, transformRow: (row, value) => applyMaterialSelection(row, value) },
    text("vendor", "Vendor", 130), finishColumn,
    computed("raw_sqft", "Raw Sq Ft", 92, row => rawSurfaceSqft(row).toFixed(2)),
    computed("material_rate", "Rate / Sq Ft", 100, row => moneyCell(MATERIAL_RATE_MAP[row.material] || 0)),
    computed("material_total", "Material Total", 110, row => moneyCell(materialLineTotal(row))),
    ...(section === "doors_fronts" ? [computed("derivation_source", "Source", 115, row => row.auto_generated ? "Auto from box" : "Manual")] : []),
    notes,
  ];
};

const formatInches = value => String(Math.round(value * 1000) / 1000);
export const deriveDoorValues = box => {
  const type = String(box.item_type || "").trim().toUpperCase();
  const width = parseMeasurement(box.width), height = parseMeasurement(box.height);
  if (!["UPPER", "BASE", "PANTRY"].includes(type) || width <= 0 || height <= 0) return null;
  const count = width < 20 ? 1 : 2;
  return {
    width: formatInches(count === 1 ? width - 0.125 : width / 2 - 0.125),
    height: formatInches(height - 0.125),
    quantity: (Number(box.quantity) || 0) * count,
    door_count_per_box: count,
  };
};

export const reconcileDerivedDoors = sections => {
  const next = Object.fromEntries(SCRATCHPAD_SECTIONS.map(item => [item.key, [...(sections?.[item.key] || [])]]));
  const existing = new Map();
  const manual = [];
  next.doors_fronts.forEach(row => {
    if (row.auto_generated && row.derived_from_box_id && !existing.has(row.derived_from_box_id)) existing.set(row.derived_from_box_id, row);
    else if (!row.auto_generated) manual.push(row);
  });
  const generated = [];
  next.cabinet_boxes.forEach(box => {
    const expected = deriveDoorValues(box);
    if (!expected || !box.row_id) return;
    const current = existing.get(box.row_id);
    const base = current ? { ...current } : applyMaterialSelection({
      ...newScratchpadRow("doors_fronts"), row_id: `auto_door_${box.row_id}`,
      item_code: box.item_code || "",
      description: `Door for ${box.description || box.item_code || "cabinet"}`,
      item_type: "DOOR", auto_generated: true, derived_from_box_id: box.row_id,
    }, "mdf_doors");
    base.item_code = box.item_code || base.item_code || "";
    delete base.part_number;
    const overrides = { ...(base.derivation_overrides || {}) };
    ["width", "height", "quantity"].forEach(field => { if (!overrides[field]) base[field] = expected[field]; });
    generated.push(normalizeScratchpadRow({
      ...base, auto_generated: true, derived_from_box_id: box.row_id, derivation_overrides: overrides,
      derived_values: { width: expected.width, height: expected.height, quantity: expected.quantity },
    }));
  });
  next.doors_fronts = [...manual, ...generated];
  return next;
};

export const hingePackagesForHeight = height => {
  const parsed = parseMeasurement(height);
  if (parsed <= 0) return 0;
  if (parsed <= 36) return 2;
  if (parsed <= 48) return 3;
  return 4;
};

export const saliceSlideSize = depth => {
  const parsed = parseMeasurement(depth);
  if (parsed <= 0) return null;
  const required = parsed + 0.75;
  return Math.min(24, Math.max(10, 10 + Math.floor(((required - 10) / 2) + 0.5) * 2));
};

export const buildHardwareManifest = sections => {
  const reconciled = reconcileDerivedDoors(sections);
  let hingePackages = 0, doorQuantity = 0;
  const breakdown = { "2_per_door": 0, "3_per_door": 0, "4_per_door": 0 };
  reconciled.doors_fronts.forEach(row => {
    if (String(row.item_type || "").trim().toUpperCase() !== "DOOR") return;
    const qty = Math.max(0, Number(row.quantity) || 0), perDoor = hingePackagesForHeight(row.height);
    if (!qty || !perDoor) return;
    const packages = qty * perDoor;
    hingePackages += packages; doorQuantity += qty; breakdown[`${perDoor}_per_door`] += packages;
  });
  const slides = {};
  reconciled.cabinet_boxes.forEach(row => {
    if (!["DRAWER", "DRW BX"].includes(String(row.item_type || "").trim().toUpperCase())) return;
    const size = saliceSlideSize(row.depth), qty = Math.max(0, Number(row.quantity) || 0);
    if (size && qty) slides[size] = (slides[size] || 0) + qty;
  });
  const lines = [];
  if (hingePackages) lines.push({ line_id: "hinge_baseplate_package", item_name: "Hinge & Baseplate Package", size: null, quantity: hingePackages, source_quantity: doorQuantity, breakdown });
  Object.keys(slides).map(Number).sort((a, b) => a - b).forEach(size => lines.push({ line_id: `salice_slide_pair_${size}`, item_name: "Salice Drawer Slide Pair", size: `${size}″`, quantity: slides[size], source_quantity: slides[size] }));
  return { lines, totals: { hinge_baseplate_packages: hingePackages, salice_slide_pairs: Object.values(slides).reduce((sum, value) => sum + value, 0) } };
};

export const discountPercentFor = baseTotal => baseTotal < 21000 ? 7 : baseTotal <= 51000 ? 4 : 2.5;
const roundMoney = value => Math.round((value + Number.EPSILON) * 100) / 100;

export const buildScratchpadLedger = (sections, settings = DEFAULT_SCRATCHPAD_SETTINGS) => {
  const rows = [];
  let materialRetail = 0, paintTotal = 0, stainTotal = 0, paintSqft = 0, stainSqft = 0, paintLinear = 0, stainLinear = 0;
  SCRATCHPAD_SECTIONS.forEach(({ key: section }) => {
    (sections?.[section] || []).forEach(source => {
      const row = normalizeScratchpadRow({ ...source, section });
      materialRetail += materialLineTotal(row);
      const finish = row.finish_required;
      if (!['paint', 'stain'].includes(finish)) return;
      if (isTrimSection(section)) {
        const linearFeet = Math.max(0, Number(row.length) || 0) * Math.max(0, Number(row.quantity) || 0);
        const frontTotal = linearFeet * FRONT_FINISH_RATES[finish];
        finish === "paint" ? (paintLinear += linearFeet, paintTotal += frontTotal) : (stainLinear += linearFeet, stainTotal += frontTotal);
        rows.push({ ...row, source_row_id: row.row_id, line_kind: "linear", linear_feet: linearFeet, front_rate: FRONT_FINISH_RATES[finish], front_total: frontTotal, back_finish: false, back_rate: 0, back_total: 0, finishing_total: frontTotal });
        return;
      }
      const width = parseMeasurement(row.width), height = parseMeasurement(row.height);
      const frontSqft = paddedFrontSqft(row), backSqft = row.back_finish ? rawSurfaceSqft(row) : 0;
      const frontTotal = frontSqft * FRONT_FINISH_RATES[finish], backTotal = backSqft * BACK_FINISH_RATES[finish];
      if (finish === "paint") { paintSqft += frontSqft + backSqft; paintTotal += frontTotal + backTotal; }
      else { stainSqft += frontSqft + backSqft; stainTotal += frontTotal + backTotal; }
      rows.push({
        ...row, source_row_id: row.row_id, line_kind: "surface", raw_width: row.width, raw_height: row.height,
        finished_width: width > 0 ? width + 2 : 0, finished_height: height > 0 ? height + 2 : 0,
        front_sqft: frontSqft, front_rate: FRONT_FINISH_RATES[finish], front_total: frontTotal,
        back_locked: WOOD_MATERIALS.has(row.material), back_sqft: backSqft, back_rate: row.back_finish ? BACK_FINISH_RATES[finish] : 0,
        back_total: backTotal, finishing_total: frontTotal + backTotal,
      });
    });
  });
  materialRetail = roundMoney(materialRetail);
  paintTotal = roundMoney(paintTotal);
  stainTotal = roundMoney(stainTotal);
  const finishingRetail = roundMoney(paintTotal + stainTotal), baseTotal = roundMoney(materialRetail + finishingRetail);
  const percent = settings.partner_discount_enabled && baseTotal > 0 ? discountPercentFor(baseTotal) : 0;
  const discountAmount = roundMoney(baseTotal * percent / 100);
  return {
    rows, material_retail_total: materialRetail, paint_sqft: paintSqft, stain_sqft: stainSqft,
    paint_linear_feet: paintLinear, stain_linear_feet: stainLinear, paint_total: paintTotal, stain_total: stainTotal,
    finishing_retail_total: finishingRetail, base_retail_total: baseTotal, partner_discount_percent: percent,
    partner_discount_amount: discountAmount, adjusted_final_balance: roundMoney(baseTotal - discountAmount),
  };
};